var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { Teleport, onBeforeUnmount, cloneVNode, isVNode, defineComponent, shallowRef, watch, computed, } from 'vue';
import { debounce } from 'throttle-debounce';
import classNames from '../_util/classNames';
import PropTypes from '../_util/vue-types';
import { filterEmpty, getPropsSlot } from '../_util/props-util';
import initDefaultProps from '../_util/props-util/initDefaultProps';
import { booleanType, objectType, someType } from '../_util/type';
import useStyle from './style';
import useConfigInject from '../config-provider/hooks/useConfigInject';
import Indicator from './Indicator';
import usePercent from './usePercent';
export const spinProps = () => ({
    prefixCls: String,
    spinning: { type: Boolean, default: undefined },
    size: String,
    /** @deprecated Please use `classNames.root` instead. */
    wrapperClassName: String,
    tip: PropTypes.any,
    delay: Number,
    indicator: PropTypes.any,
    /** Display a fullscreen backdrop with Spin (antd ≥ 5.11). */
    fullscreen: booleanType(),
    /** Progress percent; `auto` shows indeterminate mock progress (antd ≥ 5.18). */
    percent: someType([Number, String]),
    /** Semantic structure className (antd ≥ 5.23). */
    classNames: objectType(),
    /** Semantic structure style (antd ≥ 5.23). */
    styles: objectType(),
});
// Render indicator
let defaultIndicator = null;
function shouldDelay(spinning, delay) {
    return !!spinning && !!delay && !isNaN(Number(delay));
}
export function setDefaultIndicator(Content) {
    const IndicatorComp = Content.indicator;
    defaultIndicator = typeof IndicatorComp === 'function' ? IndicatorComp : () => <IndicatorComp />;
}
export default defineComponent({
    compatConfig: { MODE: 3 },
    name: 'ASpin',
    inheritAttrs: false,
    props: initDefaultProps(spinProps(), {
        size: 'default',
        spinning: true,
        wrapperClassName: '',
    }),
    setup(props, { attrs, slots }) {
        const { prefixCls, size, direction } = useConfigInject('spin', props);
        const [wrapSSR, hashId] = useStyle(prefixCls);
        const sSpinning = shallowRef(props.spinning && !shouldDelay(props.spinning, props.delay));
        let updateSpinning;
        watch([() => props.spinning, () => props.delay], () => {
            updateSpinning === null || updateSpinning === void 0 ? void 0 : updateSpinning.cancel();
            updateSpinning = debounce(props.delay, () => {
                sSpinning.value = props.spinning;
            });
            updateSpinning === null || updateSpinning === void 0 ? void 0 : updateSpinning();
        }, {
            immediate: true,
            flush: 'post',
        });
        onBeforeUnmount(() => {
            updateSpinning === null || updateSpinning === void 0 ? void 0 : updateSpinning.cancel();
        });
        const percentRef = computed(() => props.percent);
        const mergedPercent = usePercent(sSpinning, percentRef);
        return () => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            const { class: cls } = attrs, divProps = __rest(attrs, ["class"]);
            const { tip = (_a = slots.tip) === null || _a === void 0 ? void 0 : _a.call(slots) } = props;
            const children = (_b = slots.default) === null || _b === void 0 ? void 0 : _b.call(slots);
            const spinClassName = {
                [hashId.value]: true,
                [prefixCls.value]: true,
                [`${prefixCls.value}-sm`]: size.value === 'small',
                [`${prefixCls.value}-lg`]: size.value === 'large',
                [`${prefixCls.value}-spinning`]: sSpinning.value,
                [`${prefixCls.value}-show-text`]: !!tip,
                [`${prefixCls.value}-rtl`]: direction.value === 'rtl',
                [cls]: !!cls,
            };
            function renderIndicator(prefixClsName) {
                const dotClassName = `${prefixClsName}-dot`;
                let indicator = getPropsSlot(slots, props, 'indicator');
                // should not be render default indicator when indicator value is null
                if (indicator === null) {
                    return null;
                }
                if (Array.isArray(indicator)) {
                    indicator = indicator.length === 1 ? indicator[0] : indicator;
                }
                if (isVNode(indicator)) {
                    return cloneVNode(indicator, { class: dotClassName });
                }
                if (defaultIndicator && isVNode(defaultIndicator())) {
                    return cloneVNode(defaultIndicator(), { class: dotClassName });
                }
                return <Indicator prefixCls={prefixClsName} percent={mergedPercent.value}/>;
            }
            const indicatorNode = (<span class={(_c = props.classNames) === null || _c === void 0 ? void 0 : _c.indicator} style={(_d = props.styles) === null || _d === void 0 ? void 0 : _d.indicator}>
          {renderIndicator(prefixCls.value)}
        </span>);
            const spinElement = (withRootSemantic = true) => {
                var _a, _b, _c, _d;
                return (<div {...divProps} class={[spinClassName, withRootSemantic ? (_a = props.classNames) === null || _a === void 0 ? void 0 : _a.root : undefined]} style={withRootSemantic ? (_b = props.styles) === null || _b === void 0 ? void 0 : _b.root : undefined} aria-live="polite" aria-busy={sSpinning.value}>
          {indicatorNode}
          {tip ? (<div class={[`${prefixCls.value}-text`, (_c = props.classNames) === null || _c === void 0 ? void 0 : _c.tip]} style={(_d = props.styles) === null || _d === void 0 ? void 0 : _d.tip}>
              {tip}
            </div>) : null}
        </div>);
            };
            if (props.fullscreen) {
                return wrapSSR(<Teleport to="body">
            <div class={classNames(`${prefixCls.value}-fullscreen`, hashId.value, (_e = props.classNames) === null || _e === void 0 ? void 0 : _e.fullscreen, {
                        [`${prefixCls.value}-fullscreen-show`]: sSpinning.value,
                    })} style={(_f = props.styles) === null || _f === void 0 ? void 0 : _f.fullscreen}>
              {spinElement(true)}
            </div>
          </Teleport>);
            }
            if (children && filterEmpty(children).length) {
                const containerClassName = {
                    [`${prefixCls.value}-container`]: true,
                    [`${prefixCls.value}-blur`]: sSpinning.value,
                };
                return wrapSSR(<div class={[
                        `${prefixCls.value}-nested-loading`,
                        props.wrapperClassName,
                        (_g = props.classNames) === null || _g === void 0 ? void 0 : _g.root,
                        hashId.value,
                    ]} style={(_h = props.styles) === null || _h === void 0 ? void 0 : _h.root}>
            {sSpinning.value && <div key="loading">{spinElement(false)}</div>}
            <div class={containerClassName} key="container">
              {children}
            </div>
          </div>);
            }
            return wrapSSR(spinElement(true));
        };
    },
});
